import { useEffect } from 'react';
import { APIProvider, Map, Marker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { cn } from '@/lib/core/utils/cn';
import { getConfig } from '../../../../../../../../config/environment';

export interface RideMapMarker {
  lat: number;
  lng: number;
}

export interface RideMapBackdropProps {
  center: RideMapMarker;
  pickupMarker?: RideMapMarker | null;
  dropoffMarker?: RideMapMarker | null;
  /** The driver's live position, once assigned — rendered as a distinct dot marker. */
  vehicleMarker?: RideMapMarker | null;
  className?: string;
}

const ROUTE_STROKE_COLOR = '#2E1A8C';
const PICKUP_MARKER_COLOR = '#22387D';
const DROPOFF_MARKER_COLOR = '#79abca';

const PICKUP_MARKER_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32">` +
  `<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="${PICKUP_MARKER_COLOR}" stroke="#ffffff" stroke-width="1.5"/>` +
  `<circle cx="12" cy="12" r="4" fill="#ffffff"/>` +
  `</svg>`;

const DROPOFF_MARKER_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32">` +
  `<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="${DROPOFF_MARKER_COLOR}" stroke="#ffffff" stroke-width="1.5"/>` +
  `<circle cx="12" cy="12" r="4" fill="#ffffff"/>` +
  `</svg>`;

const PICKUP_MARKER_ICON: google.maps.Icon = {
  url: `data:image/svg+xml;utf8,${encodeURIComponent(PICKUP_MARKER_SVG)}`,
};
const DROPOFF_MARKER_ICON: google.maps.Icon = {
  url: `data:image/svg+xml;utf8,${encodeURIComponent(DROPOFF_MARKER_SVG)}`,
};

const VEHICLE_MARKER_COLOR = '#1A1A1A';

const VEHICLE_MARKER_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">` +
  `<circle cx="11" cy="11" r="9" fill="${VEHICLE_MARKER_COLOR}" stroke="#ffffff" stroke-width="2.5"/>` +
  `</svg>`;

const VEHICLE_MARKER_ICON: google.maps.Icon = {
  url: `data:image/svg+xml;utf8,${encodeURIComponent(VEHICLE_MARKER_SVG)}`,
  anchor: { x: 11, y: 11 } as google.maps.Point,
};

/**
 * Draws the driving route between `origin` and `destination` on the ancestor
 * `<Map>`. Fetches the route via the (non-deprecated) `DirectionsService`, then
 * renders it with a plain `Polyline` and fits the map to its bounds manually —
 * `DirectionsRenderer` itself is deprecated as of Feb 2026, see
 * https://developers.google.com/maps/documentation/javascript/routes/routes-migrate-rendering.
 * `RideMapBackdrop` keeps its own `<Marker>`s as the visible pickup/dropoff pins.
 */
function RouteRenderer({ origin, destination }: { origin: RideMapMarker; destination: RideMapMarker }) {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');

  useEffect(() => {
    if (!routesLibrary || !map) return;

    let polyline: google.maps.Polyline | undefined;
    let cancelled = false;

    new routesLibrary.DirectionsService()
      .route({ origin, destination, travelMode: google.maps.TravelMode.DRIVING })
      .then((result: { routes: any[] }) => {
        if (cancelled) return;
        const route = result.routes[0];
        if (!route) return;

        polyline = new google.maps.Polyline({
          map,
          path: route.overview_path,
          strokeColor: ROUTE_STROKE_COLOR,
          strokeWeight: 10,
        });
        map.fitBounds(route.bounds);
      })
      .catch((error: any) => console.warn('RouteRenderer: directions request failed:', error));

    return () => {
      cancelled = true;
      polyline?.setMap(null);
    };
  }, [routesLibrary, map, origin.lat, origin.lng, destination.lat, destination.lng]);

  return null;
}

/**
 * Full-bleed Google Map used as a backdrop behind the pickup/dropoff pickers.
 * Shows a marker for whichever of pickup/dropoff is set, and draws the driving
 * route between them once both are set. Panning/zooming via touch or mouse
 * drag is enabled (`gestureHandling="greedy"`); default Google chrome (zoom
 * buttons, map type control, etc.) stays hidden.
 */
export function RideMapBackdrop({
  center,
  pickupMarker,
  dropoffMarker,
  vehicleMarker,
  className,
}: RideMapBackdropProps) {
  const { googleMapsApiKey } = getConfig();

  if (!googleMapsApiKey) {
    return <div className={cn(className ?? 'absolute inset-0', 'bg-[#e5e5e5]')} />;
  }

  return (
    <APIProvider apiKey={googleMapsApiKey}>
      <Map
        className={cn(className ?? 'absolute inset-0', 'isolate')}
        defaultCenter={center}
        defaultZoom={13}
        disableDefaultUI
        gestureHandling="greedy"
        keyboardShortcuts={false}
        zoomControl={false}
      >
        {pickupMarker && <Marker position={pickupMarker} icon={PICKUP_MARKER_ICON} />}
        {dropoffMarker && <Marker position={dropoffMarker} icon={DROPOFF_MARKER_ICON} />}
        {vehicleMarker && <Marker position={vehicleMarker} icon={VEHICLE_MARKER_ICON} />}
        {pickupMarker && dropoffMarker && <RouteRenderer origin={pickupMarker} destination={dropoffMarker} />}
      </Map>
    </APIProvider>
  );
}
